import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/config";

const TOKEN_KEY = "mfg_rudder_token";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  const setToken = useCallback((newToken) => {
    if (newToken) {
      localStorage.setItem(TOKEN_KEY, newToken);
      setTokenState(newToken);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      setTokenState(null);
      setUser(null);
    }
  }, []);

  // Attach token to all axios requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setUser(res.data);
      })
      .catch(() => {
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, [token, setToken]);

  const login = useCallback(
    async (email, password) => {
      const res = await axios.post(`${API}/auth/login`, { email: email.trim().toLowerCase(), password });
      const t = res.data.access_token;
      setToken(t);
      setUser(res.data.user);
      return res.data;
    },
    [setToken]
  );

  const register = useCallback(
    async (email, password, displayName) => {
      const res = await axios.post(`${API}/auth/register`, {
        email: email.trim().toLowerCase(),
        password,
        display_name: displayName || undefined,
      });
      const data = res.data;
      // Backend does not return token for new users (they need admin to enable first)
      if (data.access_token && data.user) {
        setToken(data.access_token);
        setUser(data.user);
      }
      return data;
    },
    [setToken]
  );

  const logout = useCallback(() => {
    setToken(null);
  }, [setToken]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    setToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
